import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const DIAS_AGENDA = ['Lunes','Martes','Miercoles','Jueves','Viernes','Sabado','Domingo'];
const DIAS_SEMANA = ['Dom','Lun','Mar','Mie','Jue','Vie','Sab'];

const ESTADOS = {
  PRG:{ label:'Programado',   bg:'#6C9BD2', fg:'#fff' },
  ESP:{ label:'En espera',    bg:'#29B6F6', fg:'#fff' },
  ATN:{ label:'En atencion',  bg:'#5BAD8F', fg:'#fff' },
  FIN:{ label:'Terminado',    bg:'#A5D6A7', fg:'#333' },
  ANL:{ label:'Cancelado',    bg:'#EF9A9A', fg:'#333' },
  AUS:{ label:'Incumplido',   bg:'#7B1FA2', fg:'#fff' },
  INC:{ label:'Conf. pagada', bg:'#26A69A', fg:'#fff' },
};
const TIPOS = {
  APE:{ label:'Apertura',    bg:'#7B1FA2', fg:'#fff' },
  PRV:{ label:'Primera vez', bg:'#388E3C', fg:'#fff' },
  CNS:{ label:'Subsecuente', bg:'#1565C0', fg:'#fff' },
};

// OJO: antes esto era `d.toISOString().slice(0,10)`. toISOString() siempre
// da la fecha en UTC, no en la hora local. En Ecuador (UTC-5), desde las
// ~19:00 en adelante la hora local ya cruzó al día siguiente en UTC, así
// que esa versión devolvía la fecha de MAÑANA para columnas/citas de HOY
// (ej. un turno del lunes 24 se metía en la columna del domingo 23, porque
// la columna del "domingo" tenía por dentro un Date de las 8pm del domingo,
// que en UTC ya es lunes). Se arma la fecha con los componentes locales
// (getFullYear/getMonth/getDate), igual que ya hace fmtDia(), para que
// nunca se desincronice del día que se ve en pantalla.
const pad2        = n => String(n).padStart(2,'0');
const isoDate     = d => `${d.getFullYear()}-${pad2(d.getMonth()+1)}-${pad2(d.getDate())}`;
const addDays     = (d,n) => { const r=new Date(d); r.setDate(r.getDate()+n); return r; };
const startOfWeek = d => { const r=new Date(d); r.setDate(r.getDate()-r.getDay()); return r; };
const fmtDia      = d => `${DIAS_SEMANA[d.getDay()]} ${d.getDate()}/${d.getMonth()+1}`;
const hoyDate     = () => new Date();

// Px por minuto real, calculado según la duración de turno configurada en
// la agenda del médico (DURACION_MINUTOS) — NO un número fijo. Antes la
// grilla dibujaba 1px = 1 minuto real pero agrupaba cualquier cita a menos
// de 30px ("30 minutos") de otra en un resumen "N citas — Ver lista"; con
// turnos cada 15 minutos eso escondía casi todas las citas normales detrás
// de ese resumen. Ahora cada casilla mide al menos ~26px (legible) y su
// alto real coincide con la duración de la cita, así que las citas seguidas
// del horario normal ya no se superponen ni se ocultan.
function pxPorMinuto(agenda) {
  const dur = agenda?.DURACION_MINUTOS || 20;
  return Math.max(26 / dur, 1.3);
}

// Debajo de esta separación (en px) dos citas SÍ se consideran superpuestas
// de verdad (mismo horario, o un error de datos) y se agrupan en un resumen
// expandible. Ya no se agrupan citas que solo están seguidas cada
// 15/20/30 minutos del horario normal.
const CARD_OVERLAP_PX = 10;

// Agrupa citas de un mismo día que quedarían realmente superpuestas.
// Recibe [{t, topPx}] ya con su posición vertical calculada y devuelve
// [{topPx, items:[turno,...]}] — grupos de 1 se pintan como tarjeta normal,
// de 2+ como una franja "N citas — Ver lista" expandible.
function agruparCitasSolapadas(itemsConTop, threshold = CARD_OVERLAP_PX) {
  const ordenado = [...itemsConTop].sort((a,b)=>a.topPx-b.topPx);
  const grupos = [];
  let actual = null;
  ordenado.forEach(item=>{
    if (actual && (item.topPx - actual.ultimoTop) < threshold) {
      actual.items.push(item.t);
      actual.ultimoTop = item.topPx;
    } else {
      actual = { topPx:item.topPx, ultimoTop:item.topPx, items:[item.t] };
      grupos.push(actual);
    }
  });
  return grupos;
}

const css = `
.tl{display:flex;height:calc(100vh - 130px);background:var(--surface);border-radius:10px;border:1px solid var(--border);overflow:hidden;}
.ts{width:200px;flex-shrink:0;border-right:1px solid var(--border);display:flex;flex-direction:column;}
.ts-h{padding:10px 12px;background:var(--navy);color:#fff;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.4px;}
.ts-s{padding:4px 12px;font-size:9px;font-weight:700;text-transform:uppercase;color:var(--text3);background:var(--bg);border-bottom:1px solid var(--border);}
.tm{padding:7px 12px;font-size:11px;cursor:pointer;border-bottom:1px solid var(--bg);display:flex;align-items:center;gap:7px;}
.tm:hover{background:var(--blue-lt);}
.tm.on{background:var(--navy);color:#fff;}
.tc{flex:1;display:flex;flex-direction:column;overflow:hidden;}
.tb{padding:8px 12px;border-bottom:1px solid var(--border);display:flex;align-items:center;gap:8px;background:#fff;flex-wrap:wrap;}
.tw{font-size:13px;font-weight:700;flex:1;text-align:center;}
.tbd{flex:1;overflow:auto;}
.tgh{display:grid;grid-template-columns:44px repeat(7,1fr);position:sticky;top:0;z-index:3;min-width:580px;}
.tgh-c{padding:7px 3px;text-align:center;font-size:11px;font-weight:700;background:#1a2535;color:#fff;border-right:1px solid #2e3d52;}
.tgh-c.ht{background:var(--navy);}
.tgb{display:grid;grid-template-columns:44px repeat(7,1fr);min-width:580px;}
.tdc{border-right:1px solid var(--border);border-bottom:1px solid var(--bg);min-height:32px;padding:3px;}
.tdc.ht{background:#f8fbff;}
.tev{border-radius:3px;padding:2px 5px;font-size:10px;margin-bottom:2px;cursor:pointer;line-height:1.3;}
.tev:hover{opacity:.8;}
.tab{width:100%;padding:3px;border:1px dashed var(--border);border-radius:3px;background:transparent;font-size:10px;color:var(--text3);cursor:pointer;text-align:center;margin-top:2px;}
.tab:hover{background:var(--blue-lt);color:var(--navy);}
.mb{position:fixed;inset:0;background:rgba(0,0,0,.45);z-index:200;display:flex;align-items:center;justify-content:center;padding:16px;}
.mx{background:#fff;border-radius:10px;width:100%;max-width:500px;max-height:90vh;overflow-y:auto;box-shadow:0 10px 40px rgba(0,0,0,.25);}
.mh{padding:13px 18px;border-radius:10px 10px 0 0;display:flex;align-items:center;justify-content:space-between;}
.mbd{padding:18px;}
.mf{padding:12px 18px;border-top:1px solid var(--border);display:flex;gap:8px;justify-content:flex-end;}
.ag{display:grid;grid-template-columns:1fr 1fr;gap:12px 18px;padding:18px;}
.af{grid-column:1/-1;}
.at{width:100%;border-collapse:collapse;font-size:11px;}
.at th{background:#1a2535;color:#fff;padding:7px 10px;font-weight:600;text-align:left;}
.at td{padding:5px 7px;border-bottom:1px solid var(--border);}

.wk-total{
  display:flex;align-items:center;gap:5px;flex-shrink:0;
  background:var(--blue-lt);color:var(--navy-lt);font-size:11px;font-weight:700;
  padding:5px 11px;border-radius:16px;white-space:nowrap;
}
.wk-total b{font-size:12.5px;}

.vw-toggle{display:inline-flex;background:var(--bg);border:1px solid var(--border);border-radius:8px;padding:2px;gap:2px;}
.vw-toggle button{font-family:inherit;font-size:11.5px;font-weight:700;border:none;background:transparent;color:var(--text3);padding:5px 12px;border-radius:6px;cursor:pointer;}
.vw-toggle button.on{background:var(--navy);color:#fff;}

/* ── Barra/vista movil (oculta en escritorio; @media la activa) ── */
.tm-movil{display:none;}
.tm-sel-medico{
  width:100%;padding:9px 12px;font-size:12.5px;font-weight:600;color:var(--text);
  border:1px solid var(--border2);border-radius:8px;background:#fff;margin:10px 12px 0;
  max-width:calc(100% - 24px);
}
.tm-medico-fijo{
  margin:10px 12px 0;padding:9px 12px;font-size:12.5px;font-weight:700;color:var(--navy);
  background:var(--blue-lt);border-radius:8px;
}
.date-strip{display:flex;gap:7px;overflow-x:auto;padding:10px 12px 4px;scrollbar-width:none;}
.date-strip::-webkit-scrollbar{display:none;}
.date-chip{
  flex-shrink:0;width:50px;border-radius:11px;border:1.5px solid var(--border);
  background:#fff;padding:7px 0 6px;text-align:center;cursor:pointer;
}
.date-chip .dow{font-size:9px;font-weight:700;text-transform:uppercase;color:var(--text3);letter-spacing:.03em;}
.date-chip .num{font-size:15px;font-weight:700;color:var(--text);margin:2px 0;}
.date-chip .cnt{
  display:inline-block;font-size:9px;font-weight:700;color:var(--navy-lt);
  background:var(--blue-lt);border-radius:8px;padding:1px 6px;
}
.date-chip .cnt.zero{color:var(--text3);background:transparent;}
.date-chip.sel{background:var(--navy);border-color:var(--navy);}
.date-chip.sel .dow,.date-chip.sel .num{color:#fff;}
.date-chip.sel .cnt{color:#fff;background:rgba(255,255,255,.18);}
.day-hdr{display:flex;align-items:center;justify-content:space-between;padding:6px 12px 8px;}
.day-hdr h3{margin:0;font-size:13.5px;font-weight:700;color:var(--text);text-transform:capitalize;}
.day-hdr span{font-size:11px;color:var(--text3);font-weight:700;}
.day-list{padding:0 12px 12px;}
.day-empty{font-size:11.5px;color:var(--text3);text-align:center;padding:26px 10px;background:var(--bg);border-radius:8px;}
.day-cluster{
  background:var(--navy);color:#fff;border-radius:8px;padding:9px 12px;
  display:flex;align-items:center;justify-content:space-between;gap:8px;
  font-size:11.5px;font-weight:700;cursor:pointer;
}
.day-cluster .n{flex-shrink:0;background:rgba(255,255,255,.18);padding:2px 8px;border-radius:9px;font-size:10.5px;white-space:nowrap;}
.acc{border:1px solid var(--border);border-radius:10px;background:#fff;margin:4px 12px 12px;overflow:hidden;}
.acc summary{
  list-style:none;cursor:pointer;padding:10px 12px;font-size:11.5px;font-weight:700;
  color:var(--text2);display:flex;align-items:center;justify-content:space-between;
}
.acc summary::-webkit-details-marker{display:none;}
.acc summary::after{content:"▾";color:var(--text3);font-size:10px;}
.acc[open] summary::after{content:"▴";}
.acc-body{padding:2px 12px 12px;display:flex;flex-wrap:wrap;gap:9px 16px;}
.acc-item{display:flex;align-items:center;gap:6px;font-size:11px;color:var(--text2);}
.acc-item .sw{width:10px;height:10px;border-radius:3px;flex-shrink:0;}
.acc-item .sw.bd{background:#fff;border:2px solid;}

@media (max-width:760px){
  .tl{flex-direction:column;height:auto;min-height:calc(100vh - 130px);}
  .ts{display:none;}
  .tgh-desktop{display:none !important;}
  .tgb-desktop{display:none !important;}
  .wk-total{display:none;}
  .tm-movil{display:block;}
  .tb{gap:6px;justify-content:space-between;}
  .tw{display:none;}
  .tbd{flex:none;overflow:visible;}
}

/* ── Vista "Día" en escritorio: reusa la vista de un solo dia que ya
   existia para celular, pero a tamano de escritorio — para que un dia
   se vea completo en una sola pantalla, sin apretar 7 dias a la vez. ── */
.tl.vista-dia .tgh-desktop,.tl.vista-dia .tgb-desktop{display:none !important;}
.tl.vista-dia .tm-movil{display:block;}
.tl.vista-dia .wk-total{display:none;}
@media (min-width:761px){
  /* .tbd (la grilla semanal) es la que normalmente scrollea; en vista
     "Dia" queda vacía porque su contenido esta oculto — hay que ocultarla
     del todo y que sea .tm-movil (la lista del dia) la que ocupe el
     espacio restante y tenga su propio scroll vertical. */
  .tl.vista-dia .tbd{display:none !important;}
  .tl.vista-dia .tm-movil{display:flex;flex-direction:column;flex:1;min-height:0;overflow-y:auto;}
  .tl.vista-dia .tm-movil>*{flex-shrink:0;}
  .tl.vista-dia .tm-sel-medico,.tl.vista-dia .tm-medico-fijo{display:none;}
  .tl.vista-dia .date-strip{padding:14px 20px 6px;gap:10px;}
  .tl.vista-dia .date-chip{width:78px;padding:11px 0 10px;border-radius:14px;}
  .tl.vista-dia .date-chip .dow{font-size:10.5px;}
  .tl.vista-dia .date-chip .num{font-size:20px;}
  .tl.vista-dia .day-hdr{padding:14px 20px 12px;}
  .tl.vista-dia .day-hdr h3{font-size:17px;}
  .tl.vista-dia .day-hdr span{font-size:13px;}
  .tl.vista-dia .day-list{padding:0 20px 28px;max-width:780px;}
  .tl.vista-dia .day-empty{padding:44px 16px;font-size:13px;}
  .tl.vista-dia .acc{max-width:780px;}
}
`;

/* ── Modal Turno ─────────────────────────────────────── */
function ModalTurno({ fecha, numero, medico, servicios, convenios, turno, onGuardar, onCerrar }) {
  const esNuevo = !turno?.COD;
  const [busq, setBusq] = useState('');
  const [sugs, setSugs] = useState([]);
  const [pac,  setPac]  = useState(
    turno ? { COD:turno.COD_PACIENTE, NOMBRE_COMPLETO:turno.PACIENTE, IDENTIFICACION:turno.IDENTIFICACION } : null
  );
  const [form, setForm] = useState({
    COD_SERVICIO:    turno?.COD_SERVICIO    || (servicios[0]?.COD ?? ''),
    C_TIPO_ATENCION: turno?.C_TIPO_ATENCION || 'CNS',
    COD_CONVENIO:    turno?.COD_CONVENIO    || (convenios[0]?.COD ?? ''),
    C_ESTADO:        turno?.C_ESTADO        || 'PRG',
    HORA:            turno?.HORA            || '',
    OBSERVACION:     turno?.OBSERVACION     || '',
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  useEffect(() => {
    if (busq.length < 2) { setSugs([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get(`/pacientes?q=${encodeURIComponent(busq)}&limit=8`); setSugs(data); } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [busq]);

  const set = (k,v) => setForm(f => ({...f,[k]:v}));

  const guardar = async () => {
    if (!pac) { setErr('Seleccione un paciente'); return; }
    if (!form.COD_SERVICIO) { setErr('Seleccione un servicio'); return; }
    setSaving(true); setErr('');
    try {
      if (esNuevo) {
        await api.post('/turnos', {
          COD_PACIENTE:    pac.COD,
          FECHA:           fecha,
          HORA:            form.HORA || null,
          NUMERO:          numero,
          USUARIO_MEDICO:  medico.USUARIO,
          COD_SERVICIO:    Number(form.COD_SERVICIO),
          COD_CONVENIO:    Number(form.COD_CONVENIO) || 1,
          C_TIPO_ATENCION: form.C_TIPO_ATENCION,
          OBSERVACION:     form.OBSERVACION || null,
        });
      } else {
        // Editar: actualizar estado, convenio, tipo atencion, hora, observacion
        await api.patch(`/turnos/${turno.COD}/estado`, { C_ESTADO: form.C_ESTADO });
        await api.put(`/turnos/${turno.COD}`, {
          HORA:            form.HORA || null,
          COD_SERVICIO:    Number(form.COD_SERVICIO),
          COD_CONVENIO:    Number(form.COD_CONVENIO) || 1,
          C_TIPO_ATENCION: form.C_TIPO_ATENCION,
          OBSERVACION:     form.OBSERVACION || null,
        });
      }
      onGuardar();
    } catch(e) { setErr(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="mb" onClick={e => e.target===e.currentTarget && onCerrar()}>
      <div className="mx">
        <div className="mh" style={{background:ESTADOS[form.C_ESTADO]?.bg||'#1a2535',color:'#fff'}}>
          <span style={{fontWeight:700,fontSize:13}}>Nueva cita &mdash; {medico?.NOMBRES}</span>
          <span style={{cursor:'pointer',fontSize:18,opacity:.8}} onClick={onCerrar}>&#x2715;</span>
        </div>
        <div className="mbd">
          <div style={{background:'var(--blue-lt)',borderRadius:7,padding:'7px 12px',marginBottom:14,fontSize:12,color:'var(--navy)',display:'flex',gap:16}}>
            <span>&#128197; {fecha}</span><span>Turno <strong>#{numero}</strong></span>
          </div>

          <div style={{marginBottom:12}}>
            <label className="form-label">Paciente <span style={{color:'var(--red)'}}>*</span></label>
            {pac
              ? <div style={{display:'flex',alignItems:'center',gap:8,background:'var(--blue-lt)',borderRadius:6,padding:'6px 10px'}}>
                  <span style={{flex:1,fontSize:12,fontWeight:600}}>{pac.NOMBRE_COMPLETO?.trim()} &mdash; {pac.IDENTIFICACION}</span>
                  <span style={{cursor:'pointer',color:'var(--text3)',fontSize:16}} onClick={()=>setPac(null)}>&#x2715;</span>
                </div>
              : <div style={{position:'relative'}}>
                  <input className="form-control" placeholder="Buscar por nombre o cedula..." value={busq}
                    onChange={e=>setBusq(e.target.value)} autoFocus />
                  {sugs.length>0 && (
                    <div style={{position:'absolute',top:'100%',left:0,right:0,background:'#fff',border:'1px solid var(--border)',borderRadius:6,zIndex:10,maxHeight:200,overflowY:'auto',boxShadow:'0 4px 12px rgba(0,0,0,.1)'}}>
                      {sugs.map(p=>(
                        <div key={p.COD} onClick={()=>{setPac(p);setBusq('');setSugs([]);}}
                          style={{padding:'7px 10px',fontSize:12,cursor:'pointer',borderBottom:'1px solid var(--bg)'}}
                          onMouseEnter={e=>e.currentTarget.style.background='var(--blue-lt)'}
                          onMouseLeave={e=>e.currentTarget.style.background=''}>
                          <strong>{p.NOMBRE_COMPLETO?.trim()}</strong>
                          <span style={{color:'var(--text3)',marginLeft:8}}>{p.IDENTIFICACION}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
            }
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
            <div>
              <label className="form-label">Hora de la cita</label>
              <input type="time" className="form-control" value={form.HORA||''}
                onChange={e=>set('HORA',e.target.value)} />
            </div>
            <div>
              <label className="form-label">Tipo de atencion</label>
              <select className="form-control" value={form.C_TIPO_ATENCION} onChange={e=>set('C_TIPO_ATENCION',e.target.value)}>
                <option value="APE">Apertura</option>
                <option value="PRV">Primera vez</option>
                <option value="CNS">Subsecuente</option>
              </select>
            </div>
            <div>
              <label className="form-label">Estado</label>
              <select className="form-control" value={form.C_ESTADO} onChange={e=>set('C_ESTADO',e.target.value)}>
                {Object.entries(ESTADOS).map(([k,v])=><option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
            <div>
              <label className="form-label">Servicio</label>
              <select className="form-control" value={form.COD_SERVICIO} onChange={e=>set('COD_SERVICIO',e.target.value)}>
                <option value="">Seleccione</option>
                {servicios.map(s=><option key={s.COD} value={s.COD}>{s.SERVICIO}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Convenio</label>
              <select className="form-control" value={form.COD_CONVENIO} onChange={e=>set('COD_CONVENIO',e.target.value)}>
                {convenios.map(c=><option key={c.COD} value={c.COD}>{c.CONVENIO}</option>)}
              </select>
            </div>
          </div>

          <div style={{marginBottom:12}}>
            <label className="form-label">Observacion / Motivo</label>
            <textarea className="form-control" rows={2} value={form.OBSERVACION} onChange={e=>set('OBSERVACION',e.target.value)} />
          </div>

          {err && <div style={{color:'var(--red)',fontSize:12,marginBottom:8,padding:'6px 10px',background:'#fff5f5',borderRadius:5}}>{err}</div>}
        </div>
        <div className="mf">
          <button className="btn btn-sm" onClick={onCerrar}>Cancelar</button>
          <button className="btn btn-primary btn-sm" disabled={saving} onClick={guardar}>
            {saving?'Guardando...':'Agendar'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Modal Transferir ────────────────────────────────── */
function ModalTransferir({ turno, medicos, onGuardar, onCerrar }) {
  const [form, setForm] = useState({ FECHA:turno.FECHA, USUARIO_MEDICO:turno.USUARIO_MEDICO, NUMERO:turno.NUMERO });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  const guardar = async () => {
    setSaving(true); setErr('');
    try { await api.put(`/turnos/${turno.COD}/transferir`, form); onGuardar(); }
    catch(e) { setErr(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="mb" onClick={e=>e.target===e.currentTarget&&onCerrar()}>
      <div className="mx" style={{maxWidth:380}}>
        <div className="mh" style={{background:'#E65100',color:'#fff'}}>
          <span style={{fontWeight:700}}>Transferir turno #{turno.NUMERO}</span>
          <span style={{cursor:'pointer',fontSize:18}} onClick={onCerrar}>&#x2715;</span>
        </div>
        <div className="mbd">
          <div style={{fontSize:12,marginBottom:14,color:'var(--text2)'}}>
            Paciente: <strong>{turno.PACIENTE}</strong>
          </div>
          <div style={{display:'grid',gap:12}}>
            <div>
              <label className="form-label">Nueva fecha</label>
              <input type="date" className="form-control" value={form.FECHA}
                onChange={e=>setForm(f=>({...f,FECHA:e.target.value}))} />
            </div>
            <div>
              <label className="form-label">Medico</label>
              <select className="form-control" value={form.USUARIO_MEDICO}
                onChange={e=>setForm(f=>({...f,USUARIO_MEDICO:e.target.value}))}>
                {medicos.map(m=><option key={m.USUARIO} value={m.USUARIO}>{m.NOMBRES}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Nuevo numero de turno</label>
              <input type="number" className="form-control" min={1} max={99} value={form.NUMERO}
                onChange={e=>setForm(f=>({...f,NUMERO:Number(e.target.value)}))} />
            </div>
          </div>
          {err && <div style={{color:'var(--red)',fontSize:12,marginTop:8}}>{err}</div>}
        </div>
        <div className="mf">
          <button className="btn btn-sm" onClick={onCerrar}>Cancelar</button>
          <button className="btn btn-primary btn-sm" disabled={saving} onClick={guardar}>
            {saving?'Transfiriendo...':'Transferir'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Modal Cancelar turno ─────────────────────────────
   Cambia el estado a ANL y pide el motivo antes de hacerlo -- el cupo se
   libera solo (el backend ya excluye los turnos ANL al buscar disponibles),
   así que esto solo registra el motivo, no toca stock ni otras tablas. */
const MOTIVOS_CANCELACION = [
  'Paciente no puede asistir', 'Paciente canceló', 'Se reprogramó', 'Duplicado',
];
function ModalCancelarTurno({ turno, onGuardar, onCerrar }) {
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  const confirmar = async () => {
    setSaving(true); setErr('');
    try {
      await api.patch(`/turnos/${turno.COD}/estado`, { C_ESTADO: 'ANL', MOTIVO: motivo.trim() });
      onGuardar();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="mb" onClick={e=>e.target===e.currentTarget&&onCerrar()}>
      <div className="mx" style={{maxWidth:400}}>
        <div className="mh" style={{background:ESTADOS.ANL.bg,color:'#333'}}>
          <span style={{fontWeight:700}}>Cancelar turno #{turno.NUMERO}</span>
          <span style={{cursor:'pointer',fontSize:18}} onClick={onCerrar}>&#x2715;</span>
        </div>
        <div className="mbd">
          <div style={{fontSize:12,marginBottom:12,color:'var(--text2)'}}>
            Paciente: <strong>{turno.PACIENTE}</strong> — el cupo queda libre de inmediato para otro paciente.
          </div>
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:10}}>
            {MOTIVOS_CANCELACION.map(m=>(
              <button key={m} type="button" onClick={()=>setMotivo(m)}
                style={{padding:'4px 10px',borderRadius:20,fontSize:11,cursor:'pointer',
                  border:`1px solid ${motivo===m?'var(--navy)':'var(--border)'}`,
                  background:motivo===m?'var(--blue-lt)':'transparent',
                  color:motivo===m?'var(--navy)':'var(--text2)',fontWeight:motivo===m?600:400}}>
                {m}
              </button>
            ))}
          </div>
          <label className="form-label">Motivo</label>
          <textarea className="form-control" rows={2} placeholder="Por qué se cancela este turno..."
            value={motivo} onChange={e=>setMotivo(e.target.value)} />
          {err && <div style={{color:'var(--red)',fontSize:12,marginTop:8}}>{err}</div>}
        </div>
        <div className="mf">
          <button className="btn btn-sm" onClick={onCerrar}>Volver</button>
          <button className="btn btn-sm" disabled={saving||!motivo.trim()}
            style={{background:'#C62828',color:'#fff',border:'none'}} onClick={confirmar}>
            {saving?'Cancelando...':'Cancelar turno'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Modal Eliminar turno ──────────────────────────────
   Borrado definitivo, distinto de Cancelar -- el backend ya restringe esto
   a Administrador/DBA/Secretaría y lo rechaza si el turno ya tiene historia
   clínica o factura asociada (ahí solo cabe Cancelar). */
function ModalEliminarTurno({ turno, onGuardar, onCerrar }) {
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState('');

  const confirmar = async () => {
    setSaving(true); setErr('');
    try {
      await api.delete(`/turnos/${turno.COD}`, { data: { MOTIVO: motivo.trim() } });
      onGuardar();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="mb" onClick={e=>e.target===e.currentTarget&&onCerrar()}>
      <div className="mx" style={{maxWidth:400}}>
        <div className="mh" style={{background:'#B71C1C',color:'#fff'}}>
          <span style={{fontWeight:700}}>Eliminar turno #{turno.NUMERO}</span>
          <span style={{cursor:'pointer',fontSize:18}} onClick={onCerrar}>&#x2715;</span>
        </div>
        <div className="mbd">
          <div style={{background:'#FDECEA',border:'1px solid #F5B5B0',borderRadius:6,padding:'9px 11px',
            fontSize:11.5,color:'#7A1F17',marginBottom:12}}>
            &#9888; Esta acción no se puede deshacer. El turno de <strong>{turno.PACIENTE}</strong> se borra por
            completo, no solo se marca como cancelado. Si solo quieres liberar el cupo, usa <strong>Cancelar</strong> en su lugar.
          </div>
          <label className="form-label">Motivo (opcional)</label>
          <textarea className="form-control" rows={2} placeholder="Por qué se elimina este turno..."
            value={motivo} onChange={e=>setMotivo(e.target.value)} />
          {err && <div style={{color:'var(--red)',fontSize:12,marginTop:8}}>{err}</div>}
        </div>
        <div className="mf">
          <button className="btn btn-sm" onClick={onCerrar}>Volver</button>
          <button className="btn btn-sm" disabled={saving}
            style={{background:'#B71C1C',color:'#fff',border:'none'}} onClick={confirmar}>
            {saving?'Eliminando...':'Eliminar definitivamente'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Modal Reprogramar Agenda (masivo) ───────────────── */
function ModalReprogramarAgenda({ medicos, onCerrar, onGuardar }) {
  const [form, setForm]       = useState({ USUARIO_MEDICO:'', FECHA_ORIGEN:'', FECHA_DESTINO:'', MOTIVO:'' });
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');

  const buscarPreview = async () => {
    if (!form.USUARIO_MEDICO || !form.FECHA_ORIGEN) return;
    setLoading(true); setErr(''); setPreview([]);
    try {
      const r = await api.get('/turnos/preview-agenda', {
        params: { USUARIO_MEDICO: form.USUARIO_MEDICO, FECHA_ORIGEN: form.FECHA_ORIGEN }
      });
      setPreview(r.data);
      if (r.data.length === 0) setErr('No hay turnos activos para mover en esa fecha.');
    } catch(e) { setErr(e.response?.data?.error || e.message); }
    finally { setLoading(false); }
  };

  const confirmar = async () => {
    if (!form.FECHA_DESTINO) { setErr('Seleccioná la fecha destino.'); return; }
    setSaving(true); setErr('');
    try {
      await api.post('/turnos/transferir-agenda', form);
      onGuardar();
    } catch(e) { setErr(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="mb" onClick={e=>e.target===e.currentTarget&&onCerrar()}>
      <div className="mx" style={{maxWidth:460}}>
        <div className="mh" style={{background:'#6A1B9A',color:'#fff'}}>
          <span style={{fontWeight:700}}>Reprogramar agenda completa</span>
          <span style={{cursor:'pointer',fontSize:18}} onClick={onCerrar}>&#x2715;</span>
        </div>
        <div className="mbd">
          <div style={{display:'grid',gap:12,marginBottom:14}}>
            <div>
              <label className="form-label">Médico</label>
              <select className="form-control" value={form.USUARIO_MEDICO}
                onChange={e=>{setForm(f=>({...f,USUARIO_MEDICO:e.target.value}));setPreview([]);setErr('');}}>
                <option value="">Seleccionar médico...</option>
                {medicos.map(m=><option key={m.USUARIO} value={m.USUARIO}>{m.NOMBRES}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Fecha origen (la que se vacía)</label>
              <div style={{display:'flex',gap:8}}>
                <input type="date" className="form-control" value={form.FECHA_ORIGEN}
                  onChange={e=>{setForm(f=>({...f,FECHA_ORIGEN:e.target.value}));setPreview([]);setErr('');}} />
                <button className="btn btn-sm btn-primary" type="button"
                  onClick={buscarPreview}
                  disabled={!form.USUARIO_MEDICO||!form.FECHA_ORIGEN||loading}>
                  {loading?'...':'Buscar'}
                </button>
              </div>
            </div>
          </div>

          {loading && <div style={{fontSize:12,color:'var(--text2)',marginBottom:10}}>Buscando turnos...</div>}

          {preview.length > 0 && <>
            <div style={{fontSize:12,fontWeight:500,marginBottom:6}}>
              {preview.length} turno(s) a reprogramar:
            </div>
            <div style={{maxHeight:160,overflowY:'auto',border:'1px solid var(--border)',borderRadius:5,marginBottom:14}}>
              {preview.map((t,i)=>(
                <div key={t.COD} style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',
                  borderBottom: i<preview.length-1?'1px solid var(--border)':undefined,fontSize:12}}>
                  <span style={{minWidth:20,color:'var(--text2)'}}>#{t.NUMERO}</span>
                  <span style={{flex:1}}>{t.PACIENTE?.trim()}</span>
                  <span style={{color:'var(--text2)',fontSize:11}}>{t.SERVICIO}</span>
                </div>
              ))}
            </div>
            <div style={{display:'grid',gap:12}}>
              <div>
                <label className="form-label">Fecha destino (a dónde van)</label>
                <input type="date" className="form-control" value={form.FECHA_DESTINO}
                  onChange={e=>setForm(f=>({...f,FECHA_DESTINO:e.target.value}))} />
              </div>
              <div>
                <label className="form-label">Motivo (opcional)</label>
                <input type="text" className="form-control" placeholder="Ej: permiso médico, enfermedad..."
                  value={form.MOTIVO} onChange={e=>setForm(f=>({...f,MOTIVO:e.target.value}))} />
              </div>
            </div>
          </>}

          {err && <div style={{color:'var(--red)',fontSize:12,marginTop:8}}>{err}</div>}
        </div>
        <div className="mf">
          <button className="btn btn-sm" onClick={onCerrar}>Cancelar</button>
          {preview.length > 0 && (
            <button className="btn btn-primary btn-sm" disabled={saving||!form.FECHA_DESTINO} onClick={confirmar}>
              {saving?'Reprogramando...':'Confirmar reprogramación'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Modal Buscar Paciente ───────────────────────────── */
function ModalBuscarPaciente({ onCerrar, onVerTurno }) {
  const [busq,    setBusq]    = useState('');
  const [pacs,    setPacs]    = useState([]);
  const [turnos,  setTurnos]  = useState([]);
  const [loading, setLoading] = useState(false);
  const [pacSel,  setPacSel]  = useState(null);

  useEffect(()=>{
    if(busq.length<2){setPacs([]);return;}
    const t=setTimeout(async()=>{
      try{const{data}=await api.get(`/pacientes?q=${encodeURIComponent(busq)}&limit=8`);setPacs(data);}catch{}
    },300);
    return()=>clearTimeout(t);
  },[busq]);

  const buscarTurnos = async pac => {
    setLoading(true); setPacs([]); setPacSel(pac);
    try{const{data}=await api.get(`/turnos?paciente=${pac.COD}&limit=50`);setTurnos(data);}
    catch{setTurnos([]);}
    finally{setLoading(false);}
  };

  return (
    <div className="mb" onClick={e=>e.target===e.currentTarget&&onCerrar()}>
      <div className="mx" style={{maxWidth:580}}>
        <div className="mh" style={{background:'var(--navy)',color:'#fff'}}>
          <span style={{fontWeight:700}}>Buscar turnos por paciente</span>
          <span style={{cursor:'pointer',fontSize:18}} onClick={onCerrar}>&#x2715;</span>
        </div>
        <div className="mbd">
          <div style={{position:'relative',marginBottom:14}}>
            <input className="form-control" placeholder="Nombre o cedula del paciente..."
              value={busq} onChange={e=>setBusq(e.target.value)} autoFocus />
            {pacs.length>0 && (
              <div style={{position:'absolute',top:'100%',left:0,right:0,background:'#fff',border:'1px solid var(--border)',borderRadius:6,zIndex:10,maxHeight:200,overflowY:'auto',boxShadow:'0 4px 12px rgba(0,0,0,.1)'}}>
                {pacs.map(p=>(
                  <div key={p.COD} onClick={()=>buscarTurnos(p)}
                    style={{padding:'7px 10px',fontSize:12,cursor:'pointer',borderBottom:'1px solid var(--bg)'}}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--blue-lt)'}
                    onMouseLeave={e=>e.currentTarget.style.background=''}>
                    <strong>{p.NOMBRE_COMPLETO?.trim()}</strong>
                    <span style={{color:'var(--text3)',marginLeft:8}}>{p.IDENTIFICACION}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {pacSel && <div style={{fontSize:12,color:'var(--text2)',marginBottom:10}}>Turnos de: <strong>{pacSel.NOMBRE_COMPLETO?.trim()}</strong></div>}
          {loading && <div style={{textAlign:'center',padding:'20px 0',color:'var(--text3)'}}>Buscando...</div>}

          {turnos.length>0 && (
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'#1a2535',color:'#fff'}}>
                  {['Fecha','#','Medico','Estado','Servicio'].map(h=>(
                    <th key={h} style={{padding:'7px 10px',textAlign:'left'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {turnos.map(t=>{
                  const est=ESTADOS[t.C_ESTADO]||ESTADOS.PRG;
                  return (
                    <tr key={t.COD} style={{borderBottom:'1px solid var(--border)',cursor:'pointer'}}
                      onClick={()=>{onVerTurno(t);onCerrar();}}
                      onMouseEnter={e=>e.currentTarget.style.background='var(--blue-lt)'}
                      onMouseLeave={e=>e.currentTarget.style.background=''}>
                      <td style={{padding:'7px 10px'}}>{t.FECHA?.slice?.(0,10)||t.FECHA}</td>
                      <td style={{padding:'7px 10px'}}>{t.USUARIO_REGISTRO==='BOT_WA'&&<span title="Agendado por WhatsApp">📱 </span>}#{t.NUMERO}</td>
                      <td style={{padding:'7px 10px'}}>{t.MEDICO}</td>
                      <td style={{padding:'7px 10px'}}>
                        <span style={{background:est.bg,color:est.fg,padding:'2px 6px',borderRadius:3,fontSize:10,fontWeight:600}}>{est.label}</span>
                      </td>
                      <td style={{padding:'7px 10px',color:'var(--text2)',fontSize:11}}>{t.SERVICIO}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {!loading&&turnos.length===0&&pacSel&&(
            <div style={{textAlign:'center',padding:'20px 0',color:'var(--text3)'}}>Sin turnos encontrados</div>
          )}
        </div>
        <div className="mf">
          <button className="btn btn-sm" onClick={onCerrar}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

/* ── Form Agenda ─────────────────────────────────────── */
function FormAgenda({ agenda, medicos, servicios, onVolver, onGuardado }) {
  const esNuevo = !agenda?.COD;
  const [form, setForm] = useState({
    USUARIO_MEDICO:'',COD_SERVICIO:servicios[0]?.COD||'',NOMBRE_AGENDA:'',
    HORA_INICIO:'07:00',HORA_FIN:'13:00',DURACION_MINUTOS:20,CITAS_MAX_TURNO:1,
    VIGENCIA_DESDE:'',VIGENCIA_HASTA:'',SIN_VIGENCIA:'V',ACTIVA:'V',REPETIR_CITAS:'F',
    LUNES:'F',MARTES:'F',MIERCOLES:'F',JUEVES:'F',VIERNES:'F',SABADO:'F',DOMINGO:'F',
    ...agenda,
  });
  const [horarios,    setHorarios]    = useState(agenda?.horarios    || []);
  const [excepciones, setExcepciones] = useState(agenda?.excepciones || []);
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState('');

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const tog = d => set(d, form[d]==='V'?'F':'V');
  const setH = (i,k,v) => setHorarios(h=>h.map((r,j)=>j===i?{...r,[k]:v}:r));
  const setE = (i,k,v) => setExcepciones(e=>e.map((r,j)=>j===i?{...r,[k]:v}:r));
  const inp = {width:'100%',padding:'6px 8px',fontSize:12,border:'1px solid var(--border)',borderRadius:5};

  const guardar = async () => {
    if(!form.USUARIO_MEDICO||!form.NOMBRE_AGENDA){setMsg('Medico y nombre son obligatorios');return;}
    setSaving(true);setMsg('');
    try{
      if(esNuevo) await api.post('/turnos/agendas',{...form,horarios,excepciones});
      else        await api.put(`/turnos/agendas/${agenda.COD}`,{...form,horarios,excepciones});
      onGuardado();
    }catch(e){setMsg(e.response?.data?.error||e.message);}
    finally{setSaving(false);}
  };

  return (
    <div style={{background:'var(--surface)',borderRadius:10,border:'1px solid var(--border)'}}>
      <div style={{padding:'12px 18px',background:'var(--navy)',color:'#fff',borderRadius:'10px 10px 0 0',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <button className="btn btn-sm" style={{background:'rgba(255,255,255,.2)',color:'#fff',border:'none'}} onClick={onVolver}>&#8592; Volver</button>
          <span style={{fontSize:13,fontWeight:700}}>{esNuevo?'Nueva agenda':'Editar agenda'}</span>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {msg&&<span style={{fontSize:11,color:'#ffd'}}>{msg}</span>}
          <button className="btn btn-sm" style={{background:'#fff',color:'var(--navy)',fontWeight:700,border:'none'}} disabled={saving} onClick={guardar}>
            {saving?'Guardando...':'&#10003; Guardar'}
          </button>
        </div>
      </div>
      <div className="ag">
        <div>
          <label className="form-label">Medico *</label>
          <select className="form-control" value={form.USUARIO_MEDICO} onChange={e=>set('USUARIO_MEDICO',e.target.value)}>
            <option value="">Seleccione</option>
            {medicos.map(m=><option key={m.USUARIO} value={m.USUARIO}>{m.NOMBRES}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Nombre de agenda *</label>
          <input className="form-control" value={form.NOMBRE_AGENDA} onChange={e=>set('NOMBRE_AGENDA',e.target.value)} />
        </div>
        <div>
          <label className="form-label">Servicio</label>
          <select className="form-control" value={form.COD_SERVICIO} onChange={e=>set('COD_SERVICIO',e.target.value)}>
            {servicios.map(s=><option key={s.COD} value={s.COD}>{s.SERVICIO}</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Duracion de cita</label>
          <select className="form-control" value={form.DURACION_MINUTOS} onChange={e=>set('DURACION_MINUTOS',Number(e.target.value))}>
            {[5,10,15,20,30,45,60].map(v=><option key={v} value={v}>{v} min</option>)}
          </select>
        </div>
        <div>
          <label className="form-label">Hora inicio</label>
          <input type="time" className="form-control" value={form.HORA_INICIO} onChange={e=>set('HORA_INICIO',e.target.value)} />
        </div>
        <div>
          <label className="form-label">Hora fin</label>
          <input type="time" className="form-control" value={form.HORA_FIN} onChange={e=>set('HORA_FIN',e.target.value)} />
        </div>
        <div>
          <label className="form-label">Citas max por turno</label>
          <input type="number" className="form-control" min={1} max={99} value={form.CITAS_MAX_TURNO} onChange={e=>set('CITAS_MAX_TURNO',Number(e.target.value))} />
        </div>
        <div>
          <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,cursor:'pointer',marginBottom:6}}>
            <input type="checkbox" checked={form.SIN_VIGENCIA==='V'} onChange={e=>set('SIN_VIGENCIA',e.target.checked?'V':'F')} />
            No expira la agenda
          </label>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
            <input type="date" className="form-control" value={form.VIGENCIA_DESDE||''} disabled={form.SIN_VIGENCIA==='V'} onChange={e=>set('VIGENCIA_DESDE',e.target.value)} />
            <input type="date" className="form-control" value={form.VIGENCIA_HASTA||''} disabled={form.SIN_VIGENCIA==='V'} onChange={e=>set('VIGENCIA_HASTA',e.target.value)} />
          </div>
        </div>
        <div className="af">
          <label className="form-label">Dias de atencion</label>
          <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:4}}>
            {DIAS_AGENDA.map(d=>(
              <button key={d} onClick={()=>tog(d.toUpperCase())}
                style={{padding:'4px 12px',borderRadius:20,border:'2px solid var(--navy)',fontSize:11,fontWeight:700,cursor:'pointer',
                        background:form[d.toUpperCase()]==='V'?'var(--navy)':'transparent',
                        color:form[d.toUpperCase()]==='V'?'#fff':'var(--navy)'}}>
                {d.slice(0,3)}
              </button>
            ))}
          </div>
          <div style={{display:'flex',gap:16,marginTop:8}}>
            {[['ACTIVA','Activa'],['REPETIR_CITAS','Repetir citas']].map(([k,l])=>(
              <label key={k} style={{display:'flex',alignItems:'center',gap:5,fontSize:12,cursor:'pointer'}}>
                <input type="checkbox" checked={form[k]==='V'} onChange={e=>set(k,e.target.checked?'V':'F')} /> {l}
              </label>
            ))}
          </div>
        </div>
        <div className="af">
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
            <label className="form-label" style={{margin:0}}>Horarios</label>
            <button className="btn btn-sm" style={{background:'var(--navy)',color:'#fff',border:'none',padding:'3px 10px'}}
              onClick={()=>setHorarios(h=>[...h,{DIA:'Lunes',HORA_INICIO:'07:00',HORA_FIN:'13:00'}])}>+ Agregar</button>
          </div>
          <table className="at">
            <thead><tr><th>Dia</th><th>Hora inicio</th><th>Hora fin</th><th style={{width:36}}></th></tr></thead>
            <tbody>
              {horarios.length===0&&<tr><td colSpan={4} style={{textAlign:'center',color:'var(--text3)',padding:'10px 0'}}>Sin horarios</td></tr>}
              {horarios.map((h,i)=>(
                <tr key={i}>
                  <td><select style={inp} value={h.DIA} onChange={e=>setH(i,'DIA',e.target.value)}>{DIAS_AGENDA.map(d=><option key={d} value={d}>{d}</option>)}</select></td>
                  <td><input type="time" style={inp} value={h.HORA_INICIO} onChange={e=>setH(i,'HORA_INICIO',e.target.value)} /></td>
                  <td><input type="time" style={inp} value={h.HORA_FIN} onChange={e=>setH(i,'HORA_FIN',e.target.value)} /></td>
                  <td><button onClick={()=>setHorarios(h=>h.filter((_,j)=>j!==i))} style={{background:'var(--red)',color:'#fff',border:'none',borderRadius:4,padding:'3px 7px',cursor:'pointer'}}>&#x2715;</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="af">
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
            <label className="form-label" style={{margin:0}}>Excepciones</label>
            <button className="btn btn-sm" style={{background:'var(--navy)',color:'#fff',border:'none',padding:'3px 10px'}}
              onClick={()=>setExcepciones(e=>[...e,{FECHA:'',HORA_INICIO:'06:00',HORA_FIN:'06:15',DETALLE:''}])}>+ Agregar</button>
          </div>
          <table className="at">
            <thead><tr><th>Fecha</th><th>Hora inicio</th><th>Hora fin</th><th>Detalle</th><th style={{width:36}}></th></tr></thead>
            <tbody>
              {excepciones.length===0&&<tr><td colSpan={5} style={{textAlign:'center',color:'var(--text3)',padding:'10px 0'}}>Sin excepciones</td></tr>}
              {excepciones.map((e,i)=>(
                <tr key={i}>
                  <td><input type="date" style={inp} value={e.FECHA} onChange={v=>setE(i,'FECHA',v.target.value)} /></td>
                  <td><input type="time" style={inp} value={e.HORA_INICIO} onChange={v=>setE(i,'HORA_INICIO',v.target.value)} /></td>
                  <td><input type="time" style={inp} value={e.HORA_FIN} onChange={v=>setE(i,'HORA_FIN',v.target.value)} /></td>
                  <td><input style={inp} value={e.DETALLE||''} onChange={v=>setE(i,'DETALLE',v.target.value)} /></td>
                  <td><button onClick={()=>setExcepciones(e=>e.filter((_,j)=>j!==i))} style={{background:'var(--red)',color:'#fff',border:'none',borderRadius:4,padding:'3px 7px',cursor:'pointer'}}>&#x2715;</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ── Lista Agendas ───────────────────────────────────── */
function ListaAgendas({ medicos, servicios, onVolver }) {
  const [agendas,  setAgendas]  = useState([]);
  const [agEditar, setAgEditar] = useState(null);
  const [modo,     setModo]     = useState('lista');

  const cargar = useCallback(async()=>{
    try{const{data}=await api.get('/turnos/agendas/lista');setAgendas(data);}catch{}
  },[]);

  useEffect(()=>{cargar();},[cargar]);

  const abrirEditar = async ag => {
    const{data}=await api.get(`/turnos/agendas/${ag.COD}`);
    setAgEditar(data);setModo('form');
  };
  const eliminar = async cod => {
    if(!window.confirm('Eliminar esta agenda?'))return;
    await api.delete(`/turnos/agendas/${cod}`);cargar();
  };

  if(modo==='form') return <FormAgenda agenda={agEditar} medicos={medicos} servicios={servicios}
    onVolver={()=>setModo('lista')} onGuardado={()=>{setModo('lista');cargar();}} />;

  return (
    <div style={{background:'var(--surface)',borderRadius:10,border:'1px solid var(--border)',overflow:'hidden'}}>
      <div style={{padding:'10px 16px',background:'var(--navy)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <button className="btn btn-sm" style={{background:'rgba(255,255,255,.2)',color:'#fff',border:'none'}} onClick={onVolver}>&#8592; Calendario</button>
          <span style={{fontSize:13,fontWeight:700}}>Configuracion de agendas</span>
        </div>
        <button className="btn btn-sm" style={{background:'#fff',color:'var(--navy)',fontWeight:700,border:'none'}}
          onClick={()=>{setAgEditar(null);setModo('form');}}>+ Nueva agenda</button>
      </div>
      <div className="table-wrap">
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead>
            <tr style={{background:'#1a2535',color:'#fff'}}>
              {['Medico','Nombre agenda','Servicio','Horario','Duracion','Dias',''].map(h=>(
                <th key={h} style={{padding:'9px 12px',textAlign:'left',fontWeight:600}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {agendas.length===0&&<tr><td colSpan={7} style={{textAlign:'center',padding:'28px 0',color:'var(--text3)'}}>Sin agendas configuradas</td></tr>}
            {agendas.map(ag=>(
              <tr key={ag.COD} style={{borderBottom:'1px solid var(--border)'}}>
                <td style={{padding:'8px 12px',fontWeight:600}}>{ag.MEDICO}</td>
                <td style={{padding:'8px 12px'}}>{ag.NOMBRE_AGENDA}</td>
                <td style={{padding:'8px 12px',color:'var(--text2)'}}>{ag.SERVICIO}</td>
                <td style={{padding:'8px 12px',fontFamily:'monospace',fontSize:11}}>{ag.HORA_INICIO?.slice(0,5)} &mdash; {ag.HORA_FIN?.slice(0,5)}</td>
                <td style={{padding:'8px 12px'}}>{ag.DURACION_MINUTOS} min</td>
                <td style={{padding:'8px 12px'}}>
                  <div style={{display:'flex',gap:2}}>
                    {DIAS_AGENDA.map(d=>(
                      <span key={d} style={{padding:'1px 3px',borderRadius:2,fontSize:9,fontWeight:700,
                        background:ag[d.toUpperCase()]==='V'?'var(--navy)':'var(--bg)',
                        color:ag[d.toUpperCase()]==='V'?'#fff':'var(--text3)'}}>
                        {d.slice(0,2)}
                      </span>
                    ))}
                  </div>
                </td>
                <td style={{padding:'8px 12px'}}>
                  <div style={{display:'flex',gap:4}}>
                    <button onClick={()=>abrirEditar(ag)} style={{padding:'3px 8px',borderRadius:4,border:'1px solid var(--border)',background:'#fff',cursor:'pointer',fontSize:11}}>&#x270E;</button>
                    <button onClick={()=>eliminar(ag.COD)} style={{padding:'3px 8px',borderRadius:4,border:'none',background:'var(--red)',color:'#fff',cursor:'pointer',fontSize:11}}>&#x2715;</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Calendario ──────────────────────────────────────── */
function Calendario({ medicos, servicios, convenios, esAdmin, esMedico=false, usuarioActual='' }) {
  const navigate = useNavigate();
  const [semana, setSemana] = useState(startOfWeek(hoyDate()));
  // Inicializar medicoSel desde token inmediatamente
  const [medicoSel, setMedicoSel] = useState(()=>{
    try {
      const tk = localStorage.getItem('token');
      if(!tk) return null;
      const p = JSON.parse(atob(tk.split('.')[1]));
      if(p.esMedico) return { USUARIO: p.usuario.toUpperCase(), NOMBRES: p.nombres };
    } catch {}
    return null;
  });
  const [agenda,      setAgenda]      = useState({ HORA_INICIO:'07:00', HORA_FIN:'18:00', DURACION_MINUTOS:20 });
  // Escala real de la grilla: px por minuto según la duración de turno de
  // ESTA agenda (15, 20, 30...), y el alto (px) de una casilla de un turno.
  // Se recalculan cada vez que cambia la agenda del médico seleccionado.
  const ppm    = pxPorMinuto(agenda);
  const ROW_PX = Math.round(ppm * (agenda.DURACION_MINUTOS || 20));
  const [turnos,      setTurnos]      = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [modalNuevo,  setModalNuevo]  = useState(null);
  const [modalDet,    setModalDet]    = useState(null);
  const [modalTransf, setModalTransf] = useState(null);
  const [modalEditar, setModalEditar] = useState(null);
  const [modalCancelar, setModalCancelar] = useState(null);
  const [modalEliminar, setModalEliminar] = useState(null);
  const [modalBuscar, setModalBuscar] = useState(false);
  // Grupos de citas "muy seguidas" que el usuario expandió para verlas en lista
  // (clave: `${fecha}-${topPx del primer item del grupo}`)
  const [gruposAbiertos, setGruposAbiertos] = useState(() => new Set());
  const toggleGrupo = key => setGruposAbiertos(s => {
    const n = new Set(s);
    n.has(key) ? n.delete(key) : n.add(key);
    return n;
  });
  // Dia elegido en la vista de un solo dia (franja de fechas deslizable).
  const [diaMovilSel, setDiaMovilSel] = useState(null);
  // Vista de escritorio: 'dia' muestra un solo dia a pantalla completa
  // (misma franja de fechas y lista que ya se usaba en celular — ahi cada
  // cita tiene espacio de sobra para leerse); 'semana' muestra los 7 dias
  // en la grilla clasica, mas compacta. En celular siempre se ve un solo
  // dia sin importar este valor (el CSS lo fuerza por ancho de pantalla).
  const [vista, setVista] = useState('dia');

  const hoyStr = isoDate(hoyDate());
  const dias   = Array.from({length:7},(_,i)=>addDays(semana,i));
  const desde  = isoDate(dias[0]);
  const hasta  = isoDate(dias[6]);

  // Acceso especial: la Dra. Bombón (CBOMBON) también puede ver la agenda
  // del Dr. Gustavo, además de la suya -- pedido puntual de ella, no una
  // regla general de "los médicos ven las agendas de otros médicos" (eso
  // sigue restringido para todos los demás). Si más adelante hace falta
  // para más médicos, esto debería pasar a una tabla real de permisos
  // delegados en el backend en vez de esta lista fija en el frontend.
  const ACCESO_EXTRA_MEDICO = {
    CBOMBON: m => /GUSTAVO/i.test(m.NOMBRES || ''),
  };
  const medicosVisibles = esMedico
    ? medicos.filter(m => m.USUARIO === usuarioActual.toUpperCase()
        || ACCESO_EXTRA_MEDICO[usuarioActual.toUpperCase()]?.(m))
    : medicos;
  const puedeCambiarMedico = !esMedico || medicosVisibles.length > 1;

  const cargar = useCallback(async()=>{
    setLoading(true);
    try{
      const q=`desde=${desde}&hasta=${hasta}${medicoSel?`&medico=${medicoSel.USUARIO}`:''}`;
      const{data}=await api.get(`/turnos/semana?${q}`);
      setTurnos(data);
    }catch(e){console.error(e);}
    finally{setLoading(false);}
  },[desde,hasta,medicoSel]);

  // Cargar agenda activa del medico seleccionado
  useEffect(()=>{
    if(!medicoSel){ setAgenda({HORA_INICIO:'07:00',HORA_FIN:'18:00',DURACION_MINUTOS:20}); return; }
    api.get(`/turnos/agenda-activa?medico=${medicoSel.USUARIO}&fecha=${desde}`)
      .then(r=>setAgenda(r.data))
      .catch(()=>setAgenda({HORA_INICIO:'07:00',HORA_FIN:'18:00',DURACION_MINUTOS:20}));
  },[medicoSel, desde]);

  // Enriquecer medicoSel con datos completos cuando carguen los medicos
  useEffect(()=>{
    if(medicos.length === 0) return;
    try {
      const tk = localStorage.getItem('token');
      if(!tk) return;
      const p = JSON.parse(atob(tk.split('.')[1]));
      if(p.esMedico){
        const med = medicos.find(m=>m.USUARIO===p.usuario.toUpperCase());
        if(med) setMedicoSel(med);
      }
    } catch {}
  },[medicos]);

  useEffect(()=>{cargar();},[cargar]);

  const porFecha = {};
  turnos.forEach(t=>{if(!porFecha[t.FECHA])porFecha[t.FECHA]=[];porFecha[t.FECHA].push(t);});

  // Dia activo de la vista movil: el que el usuario tocó en la franja de
  // fechas si sigue dentro de la semana cargada; si no, hoy (si cae en la
  // semana) o el primer dia de la semana.
  const diaMovilActivo = (diaMovilSel && dias.some(d=>isoDate(d)===diaMovilSel))
    ? diaMovilSel
    : (dias.some(d=>isoDate(d)===hoyStr) ? hoyStr : desde);
  const totalCitasSemana = turnos.length;

  const abrirNuevo = async fechaStr => {
    if(!medicoSel){alert('Seleccione un medico primero (arriba, donde dice "Todos los medicos")');return;}
    try{
      const{data}=await api.get(`/turnos/medicos/disponibles?fecha=${fechaStr}&medico=${medicoSel.USUARIO}`);
      if(!data.disponibles.length){alert('No hay turnos disponibles para esta fecha');return;}
      setModalNuevo({fecha:fechaStr,numero:data.disponibles[0]});
    }catch(e){console.error(e);}
  };

  const COLORS=['#1565C0','#388E3C','#7B1FA2','#E65100','#00838F','#6D4C41','#C62828','#37474F'];

  // Abre el detalle de un turno — se usa tanto en la grilla de escritorio
  // como en la lista de dia de celular.
  const abrirDetalle = (t, e) => {
    e?.stopPropagation?.();
    const fechaTurno = t.FECHA
      ? (typeof t.FECHA === 'string' ? t.FECHA.slice(0,10) : isoDate(new Date(t.FECHA)))
      : desde;
    api.get(`/facturacion/por-paciente?cod_paciente=${t.COD_PACIENTE}&fecha=${fechaTurno}`)
      .then(r=>setModalDet({...t, COD_FACTURA: r.data?.COD || null}))
      .catch(()=>setModalDet({...t, COD_FACTURA: null}));
    api.get(`/turnos/${t.COD}`)
      .then(r=>setModalDet(md=>md&&md.COD===t.COD?{...md, historialOtroMedico:r.data?.historialOtroMedico}:md))
      .catch(()=>{});
  };

  // Tarjeta de una cita — enLista=false la posiciona absoluta sobre la
  // grilla de escritorio (por hora); enLista=true la apila en flujo normal
  // (lista de grupo expandido en escritorio, o vista de dia en celular).
  const renderCard = (t, topPx, enLista) => {
    const est=ESTADOS[t.C_ESTADO]||ESTADOS.PRG;
    const nombrePac = t.PACIENTE?.trim() || '';
    const iniciales = nombrePac ? nombrePac.split(' ').filter(Boolean).slice(0,2).map(w=>w[0]).join('').toUpperCase() : '?';
    return (
      <div key={t.COD} style={{
        position: enLista?'relative':'absolute',
        top: enLista?undefined:topPx, left:enLista?undefined:1, right:enLista?undefined:1,
        minHeight:ROW_PX, marginBottom:enLista?6:0,
        background:'var(--surface,#fff)',color:'var(--text,#1a1a1a)',
        border:'1px solid var(--border,#e2e5e9)',
        borderLeft:`4px solid ${est.bg}`,borderRadius: enLista?6:0,
        padding: enLista?'8px 10px 8px 10px':'3px 5px 3px 6px',fontSize: enLista?12.5:10,
        lineHeight:1.3,cursor:'pointer',zIndex:enLista?undefined:2,overflow:'hidden',
        display:'flex',alignItems:'center',gap: enLista?9:5,
        boxShadow:'0 1px 3px rgba(0,0,0,.15)',
      }} onClick={e=>abrirDetalle(t,e)}>
        <div style={{width:enLista?26:18,height:enLista?26:18,borderRadius:'50%',background:est.bg,color:est.fg,
          display:'flex',alignItems:'center',justifyContent:'center',fontSize:enLista?10.5:8,fontWeight:700,
          flexShrink:0}}>{iniciales}</div>
        <div style={{flex:1,minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
          {t.HORA&&<span style={{opacity:.85,marginRight:enLista?6:3,fontWeight:700}}>{t.HORA}</span>}
          {t.USUARIO_REGISTRO==='BOT_WA'&&<span title="Agendado por WhatsApp" style={{marginRight:3}}>📱</span>}<strong>#{t.NUMERO}</strong> {nombrePac.split(' ').slice(0,2).join(' ')}
        </div>
        <span style={{flexShrink:0,fontSize:enLista?9.5:8,fontWeight:700,padding: enLista?'2px 7px':'1px 5px',
          borderRadius:8,background:est.bg,color:est.fg,whiteSpace:'nowrap'}}>
          {est.label}
        </span>
      </div>
    );
  };

  // Agrupa y renderiza las citas de UN dia en formato lista vertical
  // (usa el mismo umbral de superposición real que la grilla, para que
  // "citas seguidas" se agrupe igual en escritorio y en celular).
  const renderListaDia = (fs, lista) => {
    const [iH,iM]=(agenda.HORA_INICIO||'07:00').split(':').map(Number);
    const itemsConTop = lista.map(t=>{
      let topPx=0;
      if(t.HORA){
        const [hh,mm]=t.HORA.split(':').map(Number);
        topPx=Math.max(0,((hh*60+mm)-(iH*60+iM))*ppm);
      } else {
        topPx=(t.NUMERO-1)*ROW_PX;
      }
      return { t, topPx };
    });
    const grupos = agruparCitasSolapadas(itemsConTop);
    return grupos.map(g=>{
      if (g.items.length===1) return renderCard(g.items[0], null, true);
      const key = `${fs}-${g.topPx}`;
      const abierto = gruposAbiertos.has(key);
      const horaIni = g.items[0]?.HORA || '';
      const horaFin = g.items[g.items.length-1]?.HORA || '';
      return (
        <div key={key} style={{marginBottom:8}}>
          <div className="day-cluster" onClick={()=>toggleGrupo(key)}>
            <span>🕒 {horaIni}{horaFin&&horaFin!==horaIni?`–${horaFin}`:''} · {g.items.length} citas seguidas</span>
            <span className="n">{abierto?'▴ Ocultar':'▾ Ver lista'}</span>
          </div>
          {abierto&&<div style={{marginTop:6,paddingLeft:8,borderLeft:'2px solid var(--border)'}}>
            {g.items.map(t=>renderCard(t, null, true))}
          </div>}
        </div>
      );
    });
  };

  return (
    <>
    <div className={`tl ${vista==='dia'?'vista-dia':''}`}>
      {/* Sidebar */}
      <div className="ts">
        <div className="ts-h">Agenda Medica</div>
        <div className="ts-s">Medicos</div>
        <div style={{flex:1,overflowY:'auto'}}>
          {!esMedico&&(
            <div className={`tm ${!medicoSel?'on':''}`} onClick={()=>setMedicoSel(null)}>
              <span style={{width:8,height:8,borderRadius:'50%',background:'var(--text3)',flexShrink:0}} />
              <span>Todos</span>
            </div>
          )}
          {medicosVisibles.map((m,i)=>(
            <div key={m.USUARIO} className={`tm ${medicoSel?.USUARIO===m.USUARIO?'on':''}`} onClick={()=>puedeCambiarMedico&&setMedicoSel(m)}>
              <span style={{width:8,height:8,borderRadius:'50%',background:COLORS[i%COLORS.length],flexShrink:0}} />
              <span style={{fontSize:11}}>{m.NOMBRES}</span>
            </div>
          ))}
        </div>
        <div style={{borderTop:'1px solid var(--border)',padding:'8px 12px'}}>
          <div className="ts-s" style={{padding:'3px 0',marginBottom:4}}>Tipo</div>
          {Object.entries(TIPOS).map(([k,v])=>(
            <div key={k} style={{display:'flex',alignItems:'center',gap:5,marginBottom:3}}>
              <span style={{width:10,height:10,borderRadius:2,background:v.bg,flexShrink:0}} />
              <span style={{fontSize:10,color:'var(--text2)'}}>{v.label}</span>
            </div>
          ))}
          <div className="ts-s" style={{padding:'3px 0',margin:'5px 0 4px'}}>Estado (borde)</div>
          {Object.entries(ESTADOS).slice(0,5).map(([k,v])=>(
            <div key={k} style={{display:'flex',alignItems:'center',gap:5,marginBottom:3}}>
              <span style={{width:10,height:10,borderRadius:2,background:v.bg,flexShrink:0}} />
              <span style={{fontSize:10,color:'var(--text2)'}}>{v.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Main */}
      <div className="tc">
        <div className="tb">
          <button className="btn btn-sm" onClick={()=>setSemana(s=>addDays(s,-7))}>&#8249;</button>
          <button className="btn btn-sm" onClick={()=>setSemana(s=>addDays(s,7))}>&#8250;</button>
          <button className="btn btn-sm" onClick={()=>{setSemana(startOfWeek(hoyDate()));setDiaMovilSel(hoyStr);setVista('dia');}}>Hoy</button>
          <div className="vw-toggle">
            <button className={vista==='dia'?'on':''} onClick={()=>setVista('dia')}>Día</button>
            <button className={vista==='semana'?'on':''} onClick={()=>setVista('semana')}>Semana</button>
          </div>
          <span className="tw">
            {desde.replace(/-/g,'/')} &mdash; {hasta.replace(/-/g,'/')}
            {medicoSel&&<span style={{fontSize:11,color:'var(--text3)',marginLeft:8}}>&middot; {medicoSel.NOMBRES}</span>}
            {loading&&<span style={{fontSize:11,color:'var(--text3)',marginLeft:8}}>&#8635;</span>}
          </span>
          <span className="wk-total">🗓️ <b>{totalCitasSemana}</b> citas esta semana</span>
          <button className="btn btn-sm" onClick={()=>setModalBuscar(true)}>&#128269; Paciente</button>
        </div>

        {/* ── Vista de un solo dia: medico + franja de fechas + lista del dia.
             Siempre visible en celular (CSS la fuerza); en escritorio se ve
             cuando vista==='dia' (por defecto, para que las citas se lean
             bien sin apretar 7 dias en la misma pantalla). ── */}
        <div className="tm-movil">
          {puedeCambiarMedico&&(
            <select className="tm-sel-medico" value={medicoSel?.USUARIO||''}
              onChange={e=>{
                const m = medicosVisibles.find(x=>x.USUARIO===e.target.value);
                setMedicoSel(m||null);
              }}>
              {!esMedico&&<option value="">Todos los medicos</option>}
              {medicosVisibles.map(m=>(
                <option key={m.USUARIO} value={m.USUARIO}>{m.NOMBRES}</option>
              ))}
            </select>
          )}
          {esMedico&&!puedeCambiarMedico&&medicoSel&&(
            <div className="tm-medico-fijo">👤 {medicoSel.NOMBRES}</div>
          )}

          <div className="date-strip">
            {dias.map((d,i)=>{
              const fs = isoDate(d);
              const n = porFecha[fs]?.length || 0;
              const sel = fs === diaMovilActivo;
              return (
                <div key={i} className={`date-chip ${sel?'sel':''}`} onClick={()=>setDiaMovilSel(fs)}>
                  <div className="dow">{DIAS_SEMANA[d.getDay()]}</div>
                  <div className="num">{d.getDate()}</div>
                  <div className={`cnt ${n===0?'zero':''}`}>{n}</div>
                </div>
              );
            })}
          </div>

          <div className="day-hdr">
            <h3>{fmtDia(dias.find(d=>isoDate(d)===diaMovilActivo)||dias[0])}</h3>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <span>{porFecha[diaMovilActivo]?.length||0} citas</span>
              {/* Antes, en la vista de un solo dia (celular, y "Dia" en escritorio),
                  la UNICA forma de agendar era tocar el mensaje "No hay citas este
                  dia" — que solo aparecia si el dia estaba vacio. En cuanto ya
                  existia 1 cita ese dia, esa opcion desaparecia por completo y no
                  quedaba ningun boton para agendar una mas. Este boton siempre
                  esta visible, tenga o no citas el dia. */}
              <button className="btn btn-sm" style={{background:'var(--navy)',color:'#fff',border:'none'}}
                onClick={()=>abrirNuevo(diaMovilActivo)}>+ Nuevo turno</button>
            </div>
          </div>

          <div className="day-list">
            {medicoSel && (porFecha[diaMovilActivo]||[]).length===0 && (
              <div className="day-empty" onClick={()=>abrirNuevo(diaMovilActivo)} style={{cursor:'pointer'}}>
                No hay citas este dia — toca para agendar
              </div>
            )}
            {!medicoSel && (porFecha[diaMovilActivo]||[]).length===0 && (
              <div className="day-empty">Seleccione un medico para ver o agendar citas</div>
            )}
            {renderListaDia(diaMovilActivo, porFecha[diaMovilActivo]||[])}
          </div>

          <details className="acc">
            <summary>ℹ️ Leyenda de tipos y estados</summary>
            <div className="acc-body">
              {Object.entries(TIPOS).map(([k,v])=>(
                <div key={k} className="acc-item"><span className="sw" style={{background:v.bg}} />{v.label}</div>
              ))}
              {Object.entries(ESTADOS).slice(0,5).map(([k,v])=>(
                <div key={k} className="acc-item"><span className="sw bd" style={{borderColor:v.bg}} />{v.label}</div>
              ))}
            </div>
          </details>
        </div>

        <div className="tbd">
          {/* Cabecera dias */}
          <div className="tgh-desktop" style={{display:'grid',gridTemplateColumns:'52px repeat(7,1fr)',position:'sticky',top:0,zIndex:3,minWidth:640}}>
            <div style={{background:'#1a2535',borderRight:'1px solid #2e3d52'}} />
            {dias.map((d,i)=>(
              <div key={i} className={`tgh-c ${isoDate(d)===hoyStr?'ht':''}`}>
                {fmtDia(d)}
                <div style={{fontSize:10,opacity:.7,marginTop:1}}>{porFecha[isoDate(d)]?.length||0} citas</div>
              </div>
            ))}
          </div>

          {/* Grid tiempo 7:00-18:00 */}
          <div className="tgb-desktop" style={{display:'grid',gridTemplateColumns:'52px repeat(7,1fr)',minWidth:640}}>
            {/* Columna horas — una fila por cada turno real de la agenda
                (DURACION_MINUTOS), con la hora marcada solo al inicio de
                cada hora, para que la regla de la izquierda coincida con
                el alto real de las citas de al lado. */}
            {(()=>{
              const [hiH,hiM]=(agenda.HORA_INICIO||'07:00').split(':').map(Number);
              const [hfH,hfM]=(agenda.HORA_FIN||'18:00').split(':').map(Number);
              const totalMins=(hfH*60+hfM)-(hiH*60+hiM);
              const paso=agenda.DURACION_MINUTOS||20;
              const slots=Math.ceil(totalMins/paso);
              return (
                <div style={{borderRight:'1px solid var(--border)',background:'#fafafa'}}>
                  {Array.from({length:slots},(_,i)=>{
                    const mins=hiH*60+hiM+i*paso;
                    const h=Math.floor(mins/60); const m=mins%60;
                    return (
                      <div key={i} style={{height:paso*ppm,borderBottom:m===0?'1px solid var(--border)':'1px dotted var(--bg)',
                        display:'flex',alignItems:'center',justifyContent:'flex-end',
                        paddingRight:4,fontSize:9,color:'var(--text3)'}}>
                        {m===0?`${String(h).padStart(2,'0')}:00`:''}
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Columnas por dia */}
            {dias.map((d,di)=>{
              const fs=isoDate(d);
              const lista=porFecha[fs]||[];
              const[iH,iM]=(agenda.HORA_INICIO||'07:00').split(':').map(Number);
              const[fH,fM]=(agenda.HORA_FIN||'18:00').split(':').map(Number);
              const paso=agenda.DURACION_MINUTOS||20;
              const totalSlots=Math.ceil(((fH*60+fM)-(iH*60+iM))/paso);
              const colHeight=totalSlots*paso*ppm;
              return (
                <div key={di} style={{borderRight:'1px solid var(--border)',position:'relative',
                  height:colHeight,background:fs===hoyStr?'#f8fbff':'#fff'}}>
                  {/* Lineas de fondo clicables — una por cada turno real */}
                  {Array.from({length:totalSlots},(_,i)=>(
                    <div key={i} style={{position:'absolute',top:i*paso*ppm,left:0,right:0,height:paso*ppm,
                      borderBottom:(i*paso)%60===0?'1px solid var(--border)':'1px dotted var(--bg)',
                      cursor:medicoSel?'pointer':'default'}}
                      onClick={()=>medicoSel&&abrirNuevo(fs)} />
                  ))}
                  {medicoSel&&lista.length===0&&(
                    <div style={{position:'absolute',top:0,left:0,right:0,
                      display:'flex',alignItems:'center',justifyContent:'center',
                      height:60,color:'var(--text3)',fontSize:10,cursor:'pointer'}}
                      onClick={()=>abrirNuevo(fs)}>
                      + Agendar
                    </div>
                  )}
                  {/* Eventos — agrupados solo cuando realmente se superponen */}
                  {(()=>{
                    const [iH,iM]=(agenda.HORA_INICIO||'07:00').split(':').map(Number);
                    const itemsConTop = lista.map(t=>{
                      let topPx=0;
                      if(t.HORA){
                        const [hh,mm]=t.HORA.split(':').map(Number);
                        topPx=Math.max(0,((hh*60+mm)-(iH*60+iM))*ppm);
                      } else {
                        topPx=(t.NUMERO-1)*ROW_PX;
                      }
                      return { t, topPx };
                    });
                    const grupos = agruparCitasSolapadas(itemsConTop);
                    // abrirDetalle/renderCard viven a nivel del componente
                    // Calendario — se reutilizan aqui y en la vista de dia
                    // de celular mas abajo.

                    return grupos.map(g=>{
                      if (g.items.length===1) return renderCard(g.items[0], g.topPx, false);

                      const key = `${fs}-${g.topPx}`;
                      const abierto = gruposAbiertos.has(key);
                      const horaIni = g.items[0]?.HORA || '';
                      const horaFin = g.items[g.items.length-1]?.HORA || '';
                      return (
                        <div key={key}>
                          <div style={{position:'absolute',top:g.topPx,left:1,right:1,minHeight:ROW_PX,
                            zIndex:abierto?6:2,background:'var(--navy)',color:'#fff',borderRadius:3,
                            padding:'4px 6px',fontSize:9.5,fontWeight:700,cursor:'pointer',
                            display:'flex',alignItems:'center',justifyContent:'space-between',gap:4,
                            boxShadow:'0 1px 3px rgba(0,0,0,.2)'}}
                            onClick={e=>{e.stopPropagation();toggleGrupo(key);}}>
                            <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                              🕒 {horaIni}{horaFin&&horaFin!==horaIni?`–${horaFin}`:''} · {g.items.length} citas{abierto?'':' — Ver lista'}
                            </span>
                            <span style={{flexShrink:0}}>{abierto?'▴':'▾'}</span>
                          </div>
                          {abierto&&(
                            <div style={{position:'absolute',top:g.topPx+ROW_PX+2,left:1,right:1,zIndex:7,
                              background:'#fff',border:'1px solid var(--border)',borderRadius:5,
                              boxShadow:'0 8px 20px rgba(0,0,0,.18)',maxHeight:230,overflowY:'auto',padding:3}}>
                              {g.items.map(t=>renderCard(t, null, true))}
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>

      {/* Modal nuevo turno */}
      {modalNuevo&&medicoSel&&(
        <ModalTurno fecha={modalNuevo.fecha} numero={modalNuevo.numero}
          medico={medicoSel} servicios={servicios} convenios={convenios}
          onGuardar={()=>{setModalNuevo(null);cargar();}}
          onCerrar={()=>setModalNuevo(null)} />
      )}

      {/* Modal detalle */}
      {modalDet&&(
        <div className="mb" onClick={e=>e.target===e.currentTarget&&setModalDet(null)}>
          <div className="mx" style={{maxWidth:440}}>
            <div className="mh" style={{background:ESTADOS[modalDet.C_ESTADO]?.bg||'#1a2535',color:'#fff'}}>
              <span style={{fontWeight:700}}>Turno #{modalDet.NUMERO} &mdash; {ESTADOS[modalDet.C_ESTADO]?.label}</span>
              <span style={{cursor:'pointer',fontSize:18}} onClick={()=>setModalDet(null)}>&#x2715;</span>
            </div>
            <div className="mbd">
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:14}}>
                {[['Paciente',modalDet.PACIENTE],['Cedula',modalDet.IDENTIFICACION],
                  ['Medico',modalDet.MEDICO],['Fecha',modalDet.FECHA],['Hora',modalDet.HORA],
                  ['Servicio',modalDet.SERVICIO],['Tipo',TIPOS[modalDet.C_TIPO_ATENCION]?.label],
                  ['Convenio',modalDet.CONVENIO]].map(([l,v])=>(
                  <div key={l}>
                    <div style={{fontSize:10,color:'var(--text3)',textTransform:'uppercase',fontWeight:700}}>{l}</div>
                    <div style={{fontSize:12}}>{v||'—'}</div>
                  </div>
                ))}
              </div>
              {modalDet.OBSERVACION&&<div style={{background:'var(--bg)',borderRadius:5,padding:'7px 10px',fontSize:12,marginBottom:12}}>{modalDet.OBSERVACION}</div>}

              {modalDet.historialOtroMedico?.existe && !modalDet.historialOtroMedico?.yaAutorizado && (
                <div style={{background:'#FAEEDA',border:'1px solid #EF9F27',borderRadius:5,padding:'9px 11px',marginBottom:12}}>
                  <div style={{display:'flex',gap:7,alignItems:'flex-start'}}>
                    <span style={{fontSize:14,lineHeight:1}}>&#9888;</span>
                    <div style={{fontSize:11,color:'#633806',lineHeight:1.4}}>
                      Esta paciente tiene historial registrado por{' '}
                      <strong>{modalDet.historialOtroMedico.medicos.map(m=>m.MEDICO_NOM).join(', ')}</strong>.
                      {' '}{modalDet.MEDICO} no podrá verlo sin autorización.
                    </div>
                  </div>
                  <button style={{marginTop:8,width:'100%',padding:'6px 0',borderRadius:4,border:'none',
                    background:'#BA7517',color:'#fff',fontSize:11,fontWeight:600,cursor:'pointer'}}
                    onClick={async()=>{
                      try{
                        await api.post(`/turnos/${modalDet.COD}/interconsulta`,{MOTIVO:'Autorizado desde turno'});
                        setModalDet(md=>({...md, historialOtroMedico:{...md.historialOtroMedico, yaAutorizado:true}}));
                      }catch(e){
                        alert(e.response?.data?.error||'No se pudo autorizar la interconsulta');
                      }
                    }}>
                    &#8594; Autorizar a {modalDet.MEDICO?.split(' ').slice(0,2).join(' ')}
                  </button>
                </div>
              )}

              {modalDet.historialOtroMedico?.existe && modalDet.historialOtroMedico?.yaAutorizado && (
                <div style={{background:'#EAF3DE',border:'1px solid #97C459',borderRadius:5,padding:'7px 10px',
                  marginBottom:12,fontSize:11,color:'#27500A',display:'flex',gap:6,alignItems:'center'}}>
                  <span>&#10003;</span> Interconsulta activa &mdash; {modalDet.MEDICO} ya puede ver ese historial.
                </div>
              )}

              <div style={{borderTop:'1px solid var(--border)',paddingTop:10,marginBottom:8}}>
                <div style={{fontSize:10,color:'var(--text3)',fontWeight:700,marginBottom:6}}>CAMBIAR ESTADO</div>
                <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                  {Object.entries(ESTADOS).map(([k,v])=>(
                    <button key={k} onClick={async()=>{
                      // "Cancelado" pide motivo antes de aplicarse -- ver ModalCancelarTurno.
                      if (k === 'ANL') { setModalCancelar(modalDet); setModalDet(null); return; }
                      await api.patch(`/turnos/${modalDet.COD}/estado`,{C_ESTADO:k});
                      setModalDet(null);cargar();
                    }} style={{padding:'3px 9px',borderRadius:4,border:'none',cursor:'pointer',fontSize:10,fontWeight:600,
                              background:v.bg,color:v.fg,opacity:modalDet.C_ESTADO===k?1:.6}}>
                      {v.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="mf">
              <button className="btn btn-sm" style={{background:'#E65100',color:'#fff',border:'none'}}
                onClick={()=>{setModalTransf(modalDet);setModalDet(null);}}>&#8644; Transferir</button>
              <button className="btn btn-sm" style={{background:'var(--navy)',color:'#fff',border:'none'}}
                onClick={()=>{setModalEditar(modalDet);setModalDet(null);}}>&#x270E; Editar</button>
              {!modalDet.COD_FACTURA ? (
                <button className="btn btn-sm" style={{background:'#1D9E75',color:'#fff',border:'none'}}
                  onClick={()=>{navigate(`/facturacion?paciente=${modalDet.COD_PACIENTE}&turno=${modalDet.COD}`);setModalDet(null);}}>
                  &#36; Facturar
                </button>
              ) : (
                <button className="btn btn-sm" style={{background:'#EAF3DE',color:'#27500A',border:'0.5px solid #86EFAC'}}
                  onClick={()=>{navigate('/facturacion');setModalDet(null);}}>
                  ✓ Ver factura
                </button>
              )}
              <button className="btn btn-sm" style={{background:'#534AB7',color:'#fff',border:'none'}}
                onClick={()=>{navigate(`/historia?paciente=${modalDet.COD_PACIENTE}`);setModalDet(null);}}>
                &#9998; Historia
              </button>
              {!modalDet.COD_FACTURA && !modalDet.COD_HISTORIAL_CLINICO && (
                <button className="btn btn-sm" style={{background:'#fff',color:'#B71C1C',border:'1px solid #EF9F9C'}}
                  title="Borrado definitivo -- solo Administrador, DBA o Secretaría"
                  onClick={()=>{setModalEliminar(modalDet);setModalDet(null);}}>
                  &#128465; Eliminar
                </button>
              )}
              <button className="btn btn-sm" onClick={()=>setModalDet(null)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {modalCancelar&&(
        <ModalCancelarTurno turno={modalCancelar}
          onGuardar={()=>{setModalCancelar(null);cargar();}}
          onCerrar={()=>setModalCancelar(null)} />
      )}

      {modalEliminar&&(
        <ModalEliminarTurno turno={modalEliminar}
          onGuardar={()=>{setModalEliminar(null);cargar();}}
          onCerrar={()=>setModalEliminar(null)} />
      )}

      {modalTransf&&(
        <ModalTransferir turno={modalTransf} medicos={medicos}
          onGuardar={()=>{setModalTransf(null);cargar();}}
          onCerrar={()=>setModalTransf(null)} />
      )}

      {modalEditar&&(
        <ModalTurno fecha={modalEditar.FECHA} numero={modalEditar.NUMERO}
          medico={{USUARIO:modalEditar.USUARIO_MEDICO,NOMBRES:modalEditar.MEDICO}}
          servicios={servicios} convenios={convenios}
          turno={modalEditar}
          onGuardar={()=>{setModalEditar(null);cargar();}}
          onCerrar={()=>setModalEditar(null)} />
      )}

      {modalBuscar&&(
        <ModalBuscarPaciente
          onCerrar={()=>setModalBuscar(false)}
          onVerTurno={t=>{
            const fd=new Date(t.FECHA+'T12:00:00');
            setSemana(startOfWeek(fd));
            const med=medicos.find(m=>m.USUARIO===t.USUARIO_MEDICO);
            if(med)setMedicoSel(med);
            setModalDet(t);
          }} />
      )}
    </>
  );
}

/* ── Pagina Principal ────────────────────────────────── */
export default function Turnos() {
  const [modo,       setModo]       = useState('calendario');
  const [medicos,    setMedicos]    = useState([]);
  const [servicios,  setServicios]  = useState([]);
  const [convenios,  setConvenios]  = useState([]);
  const [esAdmin,    setEsAdmin]    = useState(false);
  const [modalReprog,setModalReprog]= useState(false);

  useEffect(()=>{
    try{
      const token=localStorage.getItem('token');
      if(token){
        const p=JSON.parse(atob(token.split('.')[1]));
        setEsAdmin(p.esDBA||p.roles?.includes('DBA')||false);
        // Si es medico, preseleccionar su propia agenda
        if(p.esMedico && !p.esDBA){
          // Se filtra en Calendario via prop
        }
      }
    }catch{}
    api.get('/catalogos/medicos').then(r=>setMedicos(r.data)).catch(()=>{});
    api.get('/catalogos/servicios').then(r=>{
      const f=r.data.filter(s=>s.ES_TURNO_CONSULTA==='V');
      setServicios(f.length?f:r.data);
    }).catch(()=>{});
    api.get('/catalogos/convenios').then(r=>setConvenios(r.data)).catch(()=>{});
  },[]);

  return (
    <>
      <style>{css}</style>
      <div className="fade-up">
        <div style={{display:'flex',justifyContent:'flex-end',marginBottom:10,gap:8}}>
          <button className={`btn btn-sm ${modo==='calendario'?'btn-primary':''}`} onClick={()=>setModo('calendario')}>Calendario</button>
          {esAdmin&&(<>
            <button className="btn btn-sm" style={{background:'#6A1B9A',color:'#fff',border:'none'}}
              onClick={()=>setModalReprog(true)}>&#8635; Reprogramar agenda</button>
            <button className={`btn btn-sm ${modo==='agendas'?'btn-primary':''}`} onClick={()=>setModo('agendas')}>Configuracion de agendas</button>
          </>)}
        </div>
        {modo==='calendario'&&(()=>{
        try{
          const token=localStorage.getItem('token');
          const p=token?JSON.parse(atob(token.split('.')[1])):{};
          return <Calendario medicos={medicos} servicios={servicios} convenios={convenios}
            esAdmin={esAdmin} esMedico={p.esMedico||false} usuarioActual={p.usuario||''} />;
        }catch{ return <Calendario medicos={medicos} servicios={servicios} convenios={convenios} esAdmin={esAdmin} esMedico={false} usuarioActual='' />; }
      })()}
        {modo==='agendas'&&esAdmin&&<ListaAgendas medicos={medicos} servicios={servicios} onVolver={()=>setModo('calendario')} />}
        {modalReprog&&<ModalReprogramarAgenda medicos={medicos}
          onCerrar={()=>setModalReprog(false)}
          onGuardar={()=>{setModalReprog(false);}} />}
      </div>
    </>
  );
}
